import React, { useEffect, useRef } from 'react';
import {
  Flex,
  Box,
  Text,
  Button,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Skeleton,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import { Link as WouterLink } from 'wouter';
import dayjs from 'dayjs';
import { FaLinkedin } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClosedCaptioning } from '@fortawesome/free-regular-svg-icons';
import { useTrackedState } from '../../config/store';
import PaginationNavbar from './Pagination';

function StyledTh({ children }) {
  return (
    <Th
      fontFamily='Inter'
      fontSize='14px'
      fontWeight='700'
      letterSpacing='-0.2px'
      color='blue.500'
      textTransform='unset'
      p='12px 32px'>
      {children}
    </Th>
  );
}

function StyledTr({ children }) {
  return (
    <Tr
      _even={{ bg: 'gray.50' }}
      // cursor="pointer"
      _hover={{
        bg: 'blue.200',
        'td:first-of-type': {
          // textDecor: 'underline',
          // cursor: 'pointer',
        },
        svg: {
          visibility: 'visible',
        },
        // transition: 'background 0.2s',
        cursor: 'pointer',
      }}>
      {children}
    </Tr>
  );
}

const handleButtonClick = linkedInUrl => {
  window.open(linkedInUrl, '_blank');
  window.location.href = '/interviews';
};

function WouterCustomLink({ interviewId, children }) {
  return <WouterLink href={`/interviews/${interviewId}`}>{children}</WouterLink>;
}

function StyledNameTd({ linkedInUrl, interviewId, interviewType, children }) {
  return (
    <WouterCustomLink interviewId={interviewId}>
      <Td>
        <Flex alignItems='center'>
          <Flex flexDirection='row' width='170px'>
            <Text fontSize='14px' fontWeight='700' mr='8px' whiteSpace='break-spaces' justifyContent='space-between'>
              {children}
            </Text>
            {interviewType === 'Catch up' && (
              <Flex alignSelf='center'>
                <Tooltip
                  fontSize='sm'
                  fontWeight='400'
                  color='grey.900'
                  bg='white'
                  border='1px solid #D0D0D0'
                  boxShadow='0px 41px 60px 11px rgba(161, 161, 161, 0.160047)'
                  borderRadius='16px'
                  p='24px'
                  maxWidth='400px'
                  aria-label='A tooltip'
                  placement='bottom-start'
                  label='Catch up Call'>
                  <Icon boxSize={6}>
                    <FontAwesomeIcon icon={faClosedCaptioning} size='2xl' style={{ color: '#ff6d00' }} />
                  </Icon>
                </Tooltip>
              </Flex>
            )}
          </Flex>
          <Button variant='link' onClick={() => handleButtonClick(linkedInUrl)}>
            <Icon as={FaLinkedin} visibility='hidden' boxSize={5} />
          </Button>
        </Flex>
      </Td>
    </WouterCustomLink>
  );
}

function StyledInterviewStatusTd({ interviewStatus, interviewId }) {
  const color = {};

  switch (interviewStatus) {
    case 'Scheduled':
      color.text = 'blue.500';
      color.backgroundColor = 'blue.100';
      break;
    case 'Report Pending':
      color.text = 'orange.500';
      color.backgroundColor = 'orange.100';
      break;
    case 'Completed':
      color.text = 'green.500';
      color.backgroundColor = 'green.100';
      break;
    case 'Missing Video':
      color.text = 'yellow.500';
      color.backgroundColor = 'yellow.100';
      break;
    default:
      color.text = 'gray.500';
      color.backgroundColor = 'gray.100';
  }

  return (
    <WouterCustomLink interviewId={interviewId}>
      <Td>
        <Text
          fontWeight='700'
          color={color.text}
          as='span'
          padding='8px'
          radioGroup='2px'
          backgroundColor={color.backgroundColor}>
          {interviewStatus}
        </Text>
      </Td>
    </WouterCustomLink>
  );
}

function StyledTechStackTd({ teckStack, interviewId }) {
  return (
    <WouterCustomLink interviewId={interviewId}>
      <Td>
        <Box maxWidth='300px' display='flex' flexWrap='wrap'>
          {teckStack?.map((tech, index) => (
            <Text key={tech} marginRight='5px' as='span' display='inline-block' lineHeight='1.3'>
              {tech}
              {teckStack.length - 1 !== index ? ', ' : ''}
            </Text>
          ))}
        </Box>
      </Td>
    </WouterCustomLink>
  );
}

function StyledCommonTd({ children, interviewId }) {
  return (
    <WouterCustomLink interviewId={interviewId}>
      <Td>
        <Flex alignItems='center'>
          <Text maxWidth='150px' whiteSpace='break-spaces'>
            {children}
          </Text>
        </Flex>
      </Td>
    </WouterCustomLink>
  );
}

function SkeletonRows({ state }) {
  const skeletonRows = [];
  for (let i = 0; i <= 5; i++) {
    skeletonRows.push(
      <StyledTr key={i}>
        <Td>
          <Skeleton isLoaded={state.interviews && !state.pending}>" "</Skeleton>
        </Td>
        <Td>
          <Skeleton isLoaded={state.interviews && !state.pending}>" "</Skeleton>
        </Td>
        <Td>
          <Skeleton isLoaded={state.interviews && !state.pending}>" "</Skeleton>
        </Td>
        <Td>
          <Skeleton isLoaded={state.interviews && !state.pending}>" "</Skeleton>
        </Td>
        <Td>
          <Skeleton isLoaded={state.interviews && !state.pending}>" "</Skeleton>
        </Td>
        <Td>
          <Skeleton isLoaded={state.interviews && !state.pending}>" "</Skeleton>
        </Td>
        <Td isNumeric>
          <Skeleton isLoaded={state.interviews && !state.pending}>" "</Skeleton>
        </Td>
      </StyledTr>,
    );
  }
  return <>{skeletonRows.map(row => row)}</>;
}

function InterviewList() {
  const state = useTrackedState();
  const toast = useToast();
  const displayingToast = useRef(false);

  useEffect(() => {
    const lastToastMessageDate = localStorage.getItem('date');
    if (
      state?.interviews?.filter(
        int =>
          int.interviewData.Interview_Status === 'Missing Video' &&
          dayjs(int.interviewData.Interview_Date).isBefore(dayjs().subtract(7, 'day').endOf('day')) &&
          dayjs(int.interviewData.Interview_Date).isAfter(dayjs().subtract(3, 'month').endOf('day')),
      )?.length > 0 &&
      !displayingToast.current
    ) {
      if (displayingToast.current || dayjs(lastToastMessageDate).isSame(dayjs(), 'day')) {
        displayingToast.current = false;
      } else {
        toast({
          position: 'bottom-right',
          isClosable: true,
          title: `⏰ Don't forget to upload your interview video recordings! ⏰`,
          description: `You can find any pending interviews by filtering the status as "Missing video."`,
          status: 'warning',
          duration: null,
          preventDuplicates: true,
          variant: 'left-accent',
        });

        displayingToast.current = true;
        localStorage.setItem('date', dayjs().format());
      }
    }
  }, [state.interviews]);

  return (
    <Box>
      <TableContainer bg='white' borderRadius='8px'>
        <Table variant='striped' size='lg' colorScheme='whiteAlpha'>
          <Thead boxShadow='0px 20px 20px -14px rgba(161, 161, 161, 0.160047)'>
            <Tr>
              <StyledTh>Name</StyledTh>
              <StyledTh>Interview Status</StyledTh>
              <StyledTh>Position</StyledTh>
              <StyledTh>Seniority</StyledTh>
              <StyledTh>Tech Stack</StyledTh>
              <StyledTh>Interview Date</StyledTh>
            </Tr>
          </Thead>
          <Tbody fontSize='12px'>
            {state.interviews &&
              !state.pending &&
              state.interviews.map(interview => (
                <StyledTr key={interview.interviewData.id}>
                  <StyledNameTd
                    linkedInUrl={interview?.candidateData?.Snapshot_LinkedIn_Profile}
                    interviewId={interview.interviewData.id}
                    interviewType={interview?.interviewData.Interview_Type}>
                    {interview.interviewData.Candidate_Name?.name}
                  </StyledNameTd>
                  <StyledInterviewStatusTd
                    interviewStatus={interview?.interviewData.Interview_Status}
                    interviewId={interview.interviewData.id}
                  />
                  <StyledCommonTd interviewId={interview.interviewData.id}>
                    {interview?.candidateData?.Snapshot_Job_title}
                  </StyledCommonTd>
                  <StyledCommonTd interviewId={interview.interviewData.id}>
                    {interview?.candidateData?.Snapshot_Seniority?.join(', ')}
                  </StyledCommonTd>
                  <StyledTechStackTd
                    teckStack={interview?.candidateData?.Snapshot_Tech_stack}
                    interviewId={interview.interviewData.id}
                  />
                  <WouterCustomLink interviewId={interview.interviewData.id}>
                    <Td isNumeric>
                      {interview.interviewData?.Interview_Date.split('T')[0].split('-').reverse().join('.')}
                    </Td>
                  </WouterCustomLink>
                </StyledTr>
              ))}
            {state.pending && <SkeletonRows state={state} />}
          </Tbody>
        </Table>
      </TableContainer>
      {state?.pagination?.page && <PaginationNavbar />}
    </Box>
  );
}

export default InterviewList;
